import { isDef } from "@redotech/util/checks";
import { ChartOptions } from "chart.js";
import { createCustomTooltipHandler } from "./tooltip";
import { ChartSeries, CustomTooltipComponent, RedoChartStyle } from "./types";

const FONT = 'Inter, "Helvetica Neue", Arial, sans-serif';

export function getChartOptions({
  leftAxisLabel,
  rightAxisLabel,
  bottomAxisLabel,
  valueAxisFormatter,
  rightAxisFormatter,
  valueTooltipFormatter,
  chartStyle,
  showSeriesNames = false,
  customTooltip,
  series,
  customTooltipTitles,
  leftAxisMaxMultiplier,
  rightAxisMaxMultiplier,
  rightAxisPrecision,
  leftAxisPrecision,
  emphasizeZeroLine = false,
  leftAxisMin: fixedLeftAxisMin,
  leftAxisMax: fixedLeftAxisMax,
  rightAxisMax: fixedRightAxisMax,
}: {
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  bottomAxisLabel?: string;
  valueAxisFormatter: (value: number) => string;
  rightAxisFormatter?: (value: number) => string;
  valueTooltipFormatter: (value: number) => string;
  chartStyle: RedoChartStyle;
  showSeriesNames?: boolean;
  customTooltip?: CustomTooltipComponent;
  series?: ChartSeries[];
  customTooltipTitles?: string[];
  leftAxisMaxMultiplier?: number;
  rightAxisMaxMultiplier?: number;
  rightAxisPrecision?: number;
  leftAxisPrecision?: number;
  emphasizeZeroLine?: boolean;
  leftAxisMin?: number;
  leftAxisMax?: number;
  rightAxisMax?: number;
}): ChartOptions<"bar" | "line"> {
  // Calculate max values for each axis
  const leftAxisSeries =
    series?.filter((s) => !(s as any).yAxisId || (s as any).yAxisId === "y") ||
    [];
  const rightAxisSeries =
    series?.filter((s) => (s as any).yAxisId === "y1") || [];

  const leftAxisMax = isDef(fixedLeftAxisMax)
    ? fixedLeftAxisMax
    : leftAxisSeries.length > 0 && isDef(leftAxisMaxMultiplier)
      ? Math.max(
          ...leftAxisSeries.flatMap((s) =>
            s.data.flatMap((d) => (d.value == null ? [] : [d.value])),
          ),
        ) * leftAxisMaxMultiplier
      : undefined;

  const rightAxisMax = isDef(fixedRightAxisMax)
    ? fixedRightAxisMax
    : rightAxisSeries.length > 0 && isDef(rightAxisMaxMultiplier)
      ? Math.max(
          ...rightAxisSeries.flatMap((s) =>
            s.data.flatMap((d) => (d.value == null ? [] : [d.value])),
          ),
        ) * rightAxisMaxMultiplier
      : undefined;

  return {
    layout: { padding: { left: 0, right: 0 } },
    scales: {
      y: {
        beginAtZero: !isDef(fixedLeftAxisMin),
        position: "left",
        min: fixedLeftAxisMin,
        max: leftAxisMax,
        title: {
          display: !!leftAxisLabel,
          text: leftAxisLabel,
          font: { family: FONT, size: 14 },
        },
        ticks: {
          font: { family: FONT, size: 12 },
          callback(value) {
            return valueAxisFormatter(Number(value));
          },
          autoSkip: true,
          maxTicksLimit: 8,
          precision: leftAxisPrecision,
        },
        grid: {
          color: (context) => {
            if (emphasizeZeroLine && context.tick.value === 0) {
              return "#9CA3AF"; // Darker gray for zero line
            }
            return "rgba(0, 0, 0, 0.04)";
          },
          drawTicks: false,
        },
        border: { display: false },
      },
      ...(!!rightAxisFormatter && {
        y1: {
          type: "linear",
          beginAtZero: true,
          position: "right",
          max: rightAxisMax,
          title: {
            display: !!rightAxisLabel,
            text: rightAxisLabel,
            font: { family: FONT, size: 14 },
          },
          ticks: {
            font: { family: FONT, size: 12 },
            callback(value) {
              return rightAxisFormatter(Number(value));
            },
            autoSkip: true,
            maxTicksLimit: 8,
            precision: rightAxisPrecision,
          },
          grid: { display: false }, // Don't show grid lines for right axis
          border: { display: false },
        },
      }),
      x: {
        title: {
          display: !!bottomAxisLabel,
          text: bottomAxisLabel,
          font: { family: FONT, size: 14 },
        },
        ticks: {
          font: { family: FONT, size: 12 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
          padding: 12,
        },
        grid: { display: false },
        type: "category",
        offset: chartStyle === "bar",
        alignToPixels: true,
        border: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip:
        customTooltip && series
          ? {
              enabled: false,
              external: createCustomTooltipHandler(
                customTooltip,
                valueTooltipFormatter,
                showSeriesNames,
                series,
                customTooltipTitles,
              ),
              intersect: false,
              mode: "index",
            }
          : {
              caretSize: 5,
              padding: 10,
              bodyFont: { family: FONT },
              titleFont: { family: FONT, weight: "bold" },
              titleColor: "#ffffff",
              bodyColor: "#ffffff",
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              borderColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 0.5,
              intersect: false,
              mode: "index",
              bodyAlign: "left",
              titleAlign: "left",
              displayColors: true,
              usePointStyle: true,
              multiKeyBackground: "transparent",
              boxPadding: 6,
              boxWidth: 8,
              boxHeight: 8,
              callbacks: {
                title(tooltipItems) {
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  return tooltipItems[0].label;
                },
                label(context) {
                  const seriesText = showSeriesNames
                    ? `${context.dataset.label}: `
                    : "";
                  const value = context.parsed.y ?? 0;
                  // Use rightAxisFormatter for y1 axis series, valueTooltipFormatter for others
                  const formatter =
                    context.dataset.yAxisID === "y1" && rightAxisFormatter
                      ? rightAxisFormatter
                      : valueTooltipFormatter;
                  return `${seriesText}${formatter(value)}`;
                },
              },
            },
    },
  };
}

export const defaultDateFormatter = (date: Temporal.Instant): string => {
  return date
    .toZonedDateTimeISO("UTC")
    .toLocaleString("en-US", { month: "short", day: "numeric" });
};

export const defaultValueFormatter = (value: number): string => {
  return value.toString();
};
